/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../store";
import {
  fetchUserTrackingStats,
  fetchUserTrackingAnalytics,
  fetchAllUserTracking,
  clearErrors,
} from "../../store/slices/userTrackingSlice";

export const useUserTracking = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const {
    stats,
    statsLoading,
    statsError,
    analytics,
    analyticsLoading,
    analyticsError,
    users,
    usersLoading,
    usersError,
    usersPagination,
    lastUpdated,
  } = useSelector((state: RootState) => state.userTracking);

  // Local state
  const [activeTab, setActiveTab] = useState<
    "stats" | "analytics" | "users" | "new-users"
  >("stats");
  const [analyticsDays, setAnalyticsDays] = useState(30);
  const [statsDays, setStatsDays] = useState(30);
  const [currentPage, setCurrentPage] = useState(1);
  const [refreshing, setRefreshing] = useState(false);
  const [localLastUpdated, setLocalLastUpdated] = useState<Date | null>(null);

  // Load data based on active tab
  const loadData = async () => {
    setRefreshing(true);
    try {
      switch (activeTab) {
        case "stats":
          await dispatch(fetchUserTrackingStats(statsDays));
          break;
        case "analytics":
          await dispatch(fetchUserTrackingAnalytics(analyticsDays));
          break;
        case "users":
          await dispatch(
            fetchAllUserTracking({
              page: currentPage,
              limit: 20,
              sortBy: "lastLoginDate",
              sortOrder: "desc",
            })
          );
          break;
        case "new-users":
          await dispatch(
            fetchAllUserTracking({
              page: currentPage,
              limit: 20,
              sortBy: "accountCreatedDate",
              sortOrder: "desc",
            })
          );
          break;
      }
      setLocalLastUpdated(new Date());
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setRefreshing(false);
    }
  };

  // Load data when dependencies change
  useEffect(() => {
    loadData();
  }, [activeTab, analyticsDays, statsDays, currentPage]);

  // Clear errors when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearErrors());
    };
  }, [dispatch]);

  // Refresh handler
  const handleRefresh = () => {
    loadData();
  };

  // Tab change handler
  const handleTabChange = (
    tab: "stats" | "analytics" | "users" | "new-users"
  ) => {
    setActiveTab(tab);
    setCurrentPage(1); // Reset to first page when changing tabs
  };

  // Analytics days change handler
  const handleAnalyticsDaysChange = (days: number) => {
    setAnalyticsDays(days);
  };

  // Stats days change handler
  const handleStatsDaysChange = (days: number) => {
    setStatsDays(days);
  };

  // Page change handler
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // View user handler
  const handleViewUser = (user: any) => {
    const userId = user?.userId || user?.userId?._id || user?.userInfo?._id;
    if (!userId) return;
    // Pass tracking info so we can show app version/last login on profile
    navigate(`/user-profile/${userId}`, { state: { trackingUser: user } });
  };

  return {
    // Data
    stats,
    analytics,
    users,
    usersPagination,

    // Loading states
    statsLoading,
    analyticsLoading,
    usersLoading,
    refreshing,

    // Errors
    statsError,
    analyticsError,
    usersError,

    // UI state
    activeTab,
    analyticsDays,
    statsDays,
    currentPage,
    lastUpdated: localLastUpdated || lastUpdated,

    // Handlers
    handleTabChange,
    handleAnalyticsDaysChange,
    handleStatsDaysChange,
    handlePageChange,
    handleRefresh,
    handleViewUser,

    // Direct setters
    setActiveTab,
    setAnalyticsDays,
    setStatsDays,
    setCurrentPage,
  };
};
